import { useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { COUNTRIES } from '../../lib/countries';

/**
 * CountryPicker — searchable combobox built on top of the existing Popover
 * primitive. We keep the implementation small (no Command import) so we don't
 * pull cmdk into bundles that don't need it.
 *
 * Props:
 *   value       — the currently selected country name (controlled)
 *   onChange    — (countryName: string) => void
 *   placeholder — defaults to "Select country"
 */
export function CountryPicker({ value, onChange, placeholder = 'Select country' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const filtered = q
    ? COUNTRIES.filter((c) => c.toLowerCase().includes(q))
    : COUNTRIES;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="country-picker-trigger"
          className="w-full h-11 justify-between font-normal text-base rounded-md"
        >
          <span className={value ? '' : 'text-muted-foreground'}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(360px,90vw)] p-0 max-h-[60vh] overflow-hidden"
        data-testid="country-picker-popover"
      >
        <div className="relative border-b border-border">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            autoFocus
            type="text"
            placeholder="Search countries…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="country-picker-search"
            className="w-full h-10 pl-9 pr-3 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="overflow-y-auto max-h-[48vh]" role="listbox">
          {filtered.length === 0 && (
            <p className="px-3 py-6 text-sm text-muted-foreground text-center" data-testid="country-picker-empty">
              No match for "{query}"
            </p>
          )}
          {filtered.map((c) => (
            <button
              key={c}
              type="button"
              role="option"
              aria-selected={value === c}
              onClick={() => {
                onChange(c);
                setQuery('');
                setOpen(false);
              }}
              data-testid={`country-option-${c.replace(/\s+/g, '-')}`}
              className={`w-full px-3 h-10 flex items-center gap-2 text-left text-sm hover:bg-secondary/60 transition-colors ${
                value === c ? 'bg-secondary/40 font-medium' : ''
              }`}
            >
              <Check className={`h-4 w-4 ${value === c ? 'opacity-100 text-primary' : 'opacity-0'}`} />
              <span>{c}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
